import { useBilling, useDownloadBillingInvoice } from '@streamster/shared/api/api.portal';
import { BillingTransaction } from '@streamster/shared/api/types';
import mixins from '@streamster/shared/app/styles';
import { BusinessUserSwitcher } from '#/components/BusinessUserSwitcher/BusinessUserSwitcher';
import Button from '@streamster/shared/components/Button/Button';
import Heading from '#/components/Heading/Heading';
import Loader from '@streamster/shared/components/Loader/Loader';
import PageWrapper from '#/components/PageWrapper/PageWrapper';
import Pagination from '#/components/Pagination/Pagination';
import { formatPrice } from '@streamster/shared/utils';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Text, View } from 'react-native';
import styles from './Billing.module.scss';
import { tableStyles } from './Billing.styles';

const Table = ({ data }: { data: BillingTransaction[] }) => {
  const { mutateAsync: downloadInvoice, isPending } = useDownloadBillingInvoice();
  const [invoiceIdInDownload, setInvoiceIdInDownload] = useState<number | null>(null);

  if (!data) {
    return null;
  }

  const uppercaseFirstLetter = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const handleDownload = (transactionId: number) => {
    setInvoiceIdInDownload(transactionId);
    downloadInvoice(transactionId).finally(() => setInvoiceIdInDownload(null));
  };

  const mapTransactionToRow = (t: BillingTransaction) => {
    return (
      <View style={tableStyles.transactionRow} key={t.transactionId}>
        <Text style={tableStyles.columnDate}>
          {dayjs(t.createdAt).format('DD MMMM, YYYY HH:mm:ss')}
        </Text>
        <Text style={tableStyles.columnDescription}>{t.description}</Text>
        <Text style={tableStyles.columnMethod}>{uppercaseFirstLetter(t.paymentMethod)}</Text>
        <Text style={tableStyles.columnValue}>{formatPrice(t.value, { removeSign: true })}</Text>
        <Text style={tableStyles.columnInvoice}>
          <Button
            disabled={isPending}
            loading={invoiceIdInDownload === t.transactionId && isPending}
            secondary
            onPress={() => handleDownload(t.transactionId)}
          >
            Download
          </Button>
        </Text>
      </View>
    );
  };

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.table}>
        <View style={[tableStyles.transactionRow, tableStyles.transactionHeadingRow]}>
          <Text style={tableStyles.columnDate}>Date</Text>
          <Text style={tableStyles.columnDescription}>Description</Text>
          <Text style={tableStyles.columnMethod}>Payment method</Text>
          <Text style={tableStyles.columnValue}>Amount</Text>
          <Text style={tableStyles.columnInvoice}>Invoice</Text>
        </View>
        {data.map(mapTransactionToRow)}
      </div>
    </div>
  );
};

export const BillingScreen = () => {
  const [page, setCurrentPage] = useState(1);
  const paymentTransactions = useBilling(page);

  return (
    <PageWrapper>
      <BusinessUserSwitcher />
      <Heading style={mixins().styles.margin.topI8}>Billing history</Heading>
      {paymentTransactions?.isPending || !paymentTransactions.data?.transactions ? (
        <Loader size="large" />
      ) : paymentTransactions.data.transactions?.length > 0 ? (
        <>
          <Table data={paymentTransactions.data.transactions} />{' '}
          <div className={styles.pagination}>
            <Pagination
              currentPage={page}
              handlePageChange={setCurrentPage}
              totalPages={paymentTransactions.data.pagination.pageCount}
              pagesToDisplay={10}
            />
          </div>
        </>
      ) : (
        <Text style={tableStyles.noBillingDataText}>No billing transactions found.</Text>
      )}
    </PageWrapper>
  );
};
